import { Paper, Stack } from '@mantine/core';
import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { getReferenceString } from '@medplum/core';
import { ReactElement, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { ChipFilterBar } from '@/components/FilterChips/ChipFilterBar';
import { COHORT_ROW_CAP, QUEUE_PAGE_SIZE, shouldWarnTruncated } from '../services/cohortLoading';
import { useCohortTotal } from '../hooks/useCohortTotal';
import { CohortTruncationNotice } from './CohortTruncationNotice';
import { useChipFilters } from '@/components/FilterChips/useChipFilters';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { ALL_INQUIRY_PATIENTS_QUERY } from '../graphql';
import { usePacQueueHydration } from '../hooks/usePacQueueHydration';
import { inquiryWindowStart } from '@bariendo/lib/pac-inquiry-list';
import { readPatientCreatedAt } from '@bariendo/lib/patient-created-at';
import { buildInquiryPatientFilter, INQUIRY_CHIPS, inquirySubscriptionParams } from '../services/inquiryFilters';
import { buildInquiryRows, InquiryPatientResult } from '../services/inquiryRows';
import { getCiQueueRowProps } from './listRowStyle';
import { useInquiryColumns } from './inquiryColumns';

/**
 * The All inquiry queue — this week's sign-ups who have not booked anything.
 *
 * Fixed at seven days, with no range control. The list answers one question,
 * "who came in this week that nobody has reached", and a scope control would
 * turn it back into the Inquiry bucket — the largest population in the project.
 * Two chips, Employer benefit and Insurance. Sorted by sign-up date, newest first.
 *
 * Rooted on the Patient, because a self-pay sign-up creates nothing else. The
 * server-side window is `_lastUpdated`, a superset; the rows are narrowed to
 * real sign-ups by sign-up date, which hydration supplies — see `inquiryRows`.
 */

/** Rows per page — see `PreConsultationTab` for why pagination stays on. */
const HYDRATION_NEEDS = {
  createdAt: true,
  callNotes: true,
  tasks: true,
  eligibility: true,
  insurancePayer: true,
} as const;

export interface InquiryTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function InquiryTab({ now }: InquiryTabProps): ReactElement {
  const navigate = useNavigate();
  // Ticks, like the two CI queues (BAR-2708). The seven-day window is keyed on
  // the calendar day rather than the tick, so the query changes at midnight and
  // not every minute.
  const resolvedNow = useNow(now);
  const today = useCalendarDay(resolvedNow);
  const variables = useMemo(() => ({ filter: buildInquiryPatientFilter(today) }), [today]);
  const subscriptionParams = useMemo(() => inquirySubscriptionParams(today), [today]);

  const { data, loading, error, scrollPagination, cohortTruncated } = useLiveGraphQL(
    ALL_INQUIRY_PATIENTS_QUERY,
    variables,
    {
      enablePagination: true,
      pageSize: QUEUE_PAGE_SIZE,
      autoLoadUpTo: COHORT_ROW_CAP,
      subscriptionParams,
    }
  );

  // Rooted on Patient, unlike the two consult queues. Only the notice needs the
  // real total, and only when the cap bit.
  const cohortTotal = useCohortTotal('Patient', cohortTruncated ? variables.filter : undefined);

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the inquiry queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  const patients = useMemo(() => (error ? [] : ((data ?? []) as InquiryPatientResult[])), [data, error]);

  const patientRefs = useMemo(
    () =>
      patients
        .filter((patient) => patient.id)
        .map((patient) => getReferenceString(patient))
        .filter((reference): reference is string => reference !== undefined),
    [patients]
  );

  // No range in the scope key: this tab's window is fixed at seven days by
  // `inquiryWindowStart`, so the filter alone identifies the population.
  // Stamped patients need no history read (BAR-2787).
  const datedPatients = useMemo(
    () =>
      patients
        .filter((patient) => patient.id && readPatientCreatedAt(patient))
        .map((patient) => getReferenceString(patient))
        .filter((reference): reference is string => reference !== undefined),
    [patients]
  );

  // Last communication from the window start: every row signed up inside it, so
  // nothing older can belong to a row. The hook keys needs on content, so the
  // fresh object each render costs nothing.
  const hydration = usePacQueueHydration(
    patientRefs,
    { ...HYDRATION_NEEDS, lastCommunicationSince: inquiryWindowStart(today), datedPatients },
    `all-inquiry:${variables.filter}`
  );
  const rows = useMemo(() => buildInquiryRows(patients, hydration, resolvedNow), [patients, hydration, resolvedNow]);
  const chips = useChipFilters(INQUIRY_CHIPS, 'all-inquiry');
  const visibleRows = useMemo(() => chips.apply(rows), [chips, rows]);
  const columns = useInquiryColumns(today);

  const table = useMantineReactTable({
    columns,
    data: visibleRows,
    initialState: { density: 'xs' as const, sorting: [{ id: 'signupDate', desc: true }] },
    state: { isLoading: loading || hydration.loading },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableRowActions: false,
    enableRowSelection: false,
    enableRowVirtualization: true,
    enableToolbarInternalActions: false,
    mantineTableProps: { highlightOnHover: true },
    mantineTableBodyRowProps: ({ row }) =>
      getCiQueueRowProps(row.original, (patientId) => navigate(`/Patient/${patientId}/timeline`)),
    mantinePaperProps: {
      shadow: 'sm',
      withBorder: true,
      style: { height: '100%', display: 'flex', flexDirection: 'column' },
    },
    mantineTableContainerProps: scrollPagination?.tableContainerProps,
    mantineTableHeadCellProps: { style: { fontWeight: '500' } },
  });

  return (
    <Stack gap="sm" style={{ flex: 1, overflow: 'hidden' }}>
      {/*
        The count is every row in the seven-day cohort that passes the chip, as on
        the other two queues — the tab loads the whole cohort rather than a page
        (BAR-2738). This queue is the least likely of the three to ever truncate,
        but it carries the same notice so they cannot drift apart.
      */}
      {shouldWarnTruncated(cohortTruncated, rows.length, cohortTotal) && (
        <CohortTruncationNotice loaded={rows.length} total={cohortTotal} />
      )}
      <ChipFilterBar state={chips} count={visibleRows.length} />
      <Paper style={{ flex: 1, overflow: 'hidden' }}>
        <MantineReactTable table={table} />
      </Paper>
    </Stack>
  );
}
